import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Chip,
  Divider,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

// ─── Constants ────────────────────────────────────────────────────────────────

const MODEL_COLORS = [
  '#2E77D0', '#E85D75', '#43A047', '#FB8C00', '#8E24AA',
  '#00ACC1', '#D81B60', '#7CB342', '#F4511E', '#3949AB',
  '#00897B', '#C0CA33', '#6D4C41', '#546E7A', '#EC407A',
];

const YEAR_COLORS = [
  '#1565C0', '#2E7D32', '#E65100', '#6A1B9A', '#00838F',
  '#AD1457', '#558B2F', '#D84315', '#283593', '#00695C',
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface CompositionRow {
  make: string;
  model: string;
  model_year: string;
  active_count: number;
  deleted_count: number;
  total_count: number;
}

interface CompositionResponse {
  data: CompositionRow[];
  summary: {
    total_vehicles: number;
    active_vehicles: number;
    deleted_vehicles: number;
    unique_models: number;
    by_platform?: Record<string, number>;
  };
  chart_data: { model: string; [year: string]: number | string }[];
  csv_content: string;
}

interface UsageRow {
  vin: string;
  make: string;
  model: string;
  model_year: string;
  platform: string;
  package: string;
  package_status: string;
  vehicle_status: string;
  enrollment_status: string;
  connection: string;
  is_deleted: boolean;
  last_odometer: number | null;
  avg_miles_per_month: number | null;
  avg_days_per_month: number | null;
}

interface MetricsByModelRow {
  model: string;
  total: number;
  active: number;
  deleted: number;
  with_miles_data: number;
  avg_miles: number | null;
  with_defleeting_odo: number;
  avg_defleeting_odo: number | null;
}

interface DefleetForecastByModel {
  model: string;
  defleet_threshold: number | null;
  active: number;
  predictable: number;
  past_threshold: number;
  month_1: number;
  month_2: number;
  month_3: number;
}

interface DefleetForecast {
  predictions: { past_threshold: number; month_1: number; month_2: number; month_3: number };
  coverage: { total_active: number; predictable: number; missing_odometer: number; missing_threshold: number };
  by_model: DefleetForecastByModel[];
}

interface FleetAnalyticsSummary {
  summary: {
    total_vehicles: number;
    total_active: number;
    total_deleted: number;
    avg_monthly_miles: number | null;
    vehicles_with_miles: number;
    avg_days_driven: number | null;
    vehicles_with_days: number;
    avg_defleeting_odometer: number | null;
    vehicles_with_defleeting_odo: number;
    vehicles_with_odometer: number;
  };
  metrics_by_model: MetricsByModelRow[];
  defleet_forecast: DefleetForecast;
  last_computed_at: string | null;
}

interface UsageResponse {
  data: UsageRow[];
  total_count: number;
  has_more: boolean;
  summary: {
    total_vehicles: number;
    total_active: number;
    total_deleted: number;
    avg_monthly_miles: number | null;
    vehicles_with_miles: number;
    avg_days_driven: number | null;
    vehicles_with_days: number;
    avg_defleeting_odometer: number | null;
    vehicles_with_defleeting_odo: number;
    vehicles_with_odometer: number;
    by_vehicle_status: Record<string, number>;
    by_enrollment: Record<string, number>;
    by_connection: Record<string, number>;
    by_package: Record<string, number>;
    by_package_status: Record<string, number>;
  };
  by_platform?: Record<string, number>;
  csv_content: string;
}

interface MovementRow {
  month: string;
  model: string;
  model_year: string;
  added: number;
  deleted: number;
  net: number;
}

interface MovementResponse {
  data: MovementRow[];
  summary: {
    total_added: number;
    total_deleted: number;
    net_change: number;
  };
  chart_monthly: { month: string; added: number; deleted: number }[];
  chart_by_model: { month: string; [key: string]: number | string }[];
  models: string[];
  by_platform?: Record<string, number>;
  csv_content: string;
}

interface HealthAlertDetail {
  vin: string;
  indicator: string;
  first_seen: string;
  last_seen: string;
  event_count: number;
}

interface HealthResponse {
  fleet_breakdown: { total_ccs2: number; defleeted: number; non_defleeted: number };
  health_coverage: { with_health_data: number; without_health_data: number };
  health_summary: {
    vehicles_with_alerts: number;
    vehicles_all_healthy: number;
    indicator_counts: Record<string, number>;
  };
  alert_vins: string[];
  history_30d?: {
    unique_vins_with_alerts: number;
    indicator_counts: Record<string, number>;
    alert_details: HealthAlertDetail[];
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function SummaryCard({
  title,
  value,
  subtitle,
  color,
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  color?: string;
}) {
  return (
    <Card sx={{ minWidth: 160, flex: '1 1 160px', border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary', mt: 0.5 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function downloadCsv(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function apiFetch<T>(endpoint: string, params: Record<string, string | number | boolean>): Promise<T> {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== '' && v !== undefined && v !== null) query.set(k, String(v));
  }
  const url = `/api/fleet-intelligence/${endpoint}?${query.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `API error ${res.status}`);
  }
  return res.json();
}

// ─── Filter Bar ───────────────────────────────────────────────────────────────

interface FilterState {
  make: string;
  model: string;
  model_year: string;
  platform: string;
  date_from: string;
  date_to: string;
  include_deleted: string; // '' = all, 'true' = include, 'false' = exclude, 'only' = deleted_only
}

const INITIAL_FILTER_STATE: FilterState = {
  make: '',
  model: '',
  model_year: '',
  platform: '',
  date_from: '',
  date_to: '',
  include_deleted: '',
};

function FilterBar({
  filters,
  setFilters,
  onSearch,
  loading,
  extraFilters,
  onReset,
}: {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  onSearch: () => void;
  loading: boolean;
  extraFilters?: React.ReactNode;
  onReset?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = [filters.make, filters.model, filters.model_year, filters.platform, filters.date_from, filters.date_to, filters.include_deleted].filter(Boolean).length;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant={activeCount > 0 ? 'contained' : 'outlined'}
          color={activeCount > 0 ? 'primary' : 'inherit'}
          startIcon={<FilterListIcon />}
          onClick={() => setOpen((p) => !p)}
        >
          Filters{activeCount > 0 ? ` (${activeCount})` : ''}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={onSearch}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
        {onReset && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={onReset}
            disabled={loading}
          >
            Reset Filters
          </Button>
        )}
      </Box>
      <Collapse in={open}>
        <Card sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Make"
              placeholder="e.g. NISSAN"
              value={filters.make}
              onChange={(e) => setFilters((p) => ({ ...p, make: e.target.value.toUpperCase() }))}
              sx={{ width: 140 }}
            />
            <TextField
              size="small"
              label="Model"
              placeholder="e.g. ROGUE"
              value={filters.model}
              onChange={(e) => setFilters((p) => ({ ...p, model: e.target.value.toUpperCase() }))}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label="Model Year"
              placeholder="e.g. 2024"
              value={filters.model_year}
              onChange={(e) => setFilters((p) => ({ ...p, model_year: e.target.value }))}
              sx={{ width: 120 }}
            />
            <FormControl size="small" sx={{ width: 160 }}>
              <InputLabel>Deleted</InputLabel>
              <Select
                value={filters.include_deleted}
                label="Deleted"
                onChange={(e) => setFilters((p) => ({ ...p, include_deleted: e.target.value }))}
              >
                <MenuItem value="">All (incl. deleted)</MenuItem>
                <MenuItem value="false">Active Only</MenuItem>
                <MenuItem value="only">Deleted Only</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="From"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))}
              sx={{ width: 150 }}
            />
            <TextField
              size="small"
              label="To"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.date_to}
              onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))}
              sx={{ width: 150 }}
            />
            {extraFilters}
            <Button variant="contained" size="small" startIcon={<SearchIcon />} onClick={onSearch}>
              Search
            </Button>
          </Box>
        </Card>
      </Collapse>
    </>
  );
}

function buildFilterParams(f: FilterState): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (f.make) params.make = f.make;
  if (f.model) params.model = f.model;
  if (f.model_year) params.model_year = f.model_year;
  if (f.platform) params.platform = f.platform;
  if (f.date_from) params.date_from = f.date_from;
  if (f.date_to) params.date_to = f.date_to;
  if (f.include_deleted === 'false') {
    params.include_deleted = false;
  } else if (f.include_deleted === 'only') {
    params.deleted_only = true;
  } else {
    params.include_deleted = true;
  }
  return params;
}

// =============================================================================
// Tab 1: Fleet Composition
// =============================================================================

// Simple Tooltip for Model Chart (hover only shows total)
function SimpleModelTooltip({ active, payload }: any) {
  if (!active || !payload?.[0]) return null;
  const data = payload[0].payload;
  return (
    <Paper sx={{ p: 1, boxShadow: 2 }}>
      <Typography variant="body2" fontWeight={600}>
        {data.model}
      </Typography>
      <Typography variant="caption" color="primary">
        {data.total.toLocaleString()} vehicles
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
        Click for year breakdown
      </Typography>
    </Paper>
  );
}

function FleetCompositionTab() {
  const [data, setData] = useState<CompositionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTER_STATE);
  const fetched = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch<CompositionResponse>('composition', buildFilterParams(filters));
      setData(result);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!fetched.current) { fetched.current = true; fetchData(); }
  }, [fetchData]);

  const handleReset = () => {
    setFilters(INITIAL_FILTER_STATE);
    setSelectedModel(null);
    setLoading(true);
    setError(null);
    apiFetch<CompositionResponse>('composition', buildFilterParams(INITIAL_FILTER_STATE))
      .then(setData)
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const summary = data?.summary;
  const byPlatform = summary?.by_platform;
  const platformOptions = byPlatform ? Object.keys(byPlatform).sort() : [];
  const extraFilters = (
    <FormControl size="small" sx={{ width: 160 }}>
      <InputLabel>Platform</InputLabel>
      <Select
        value={filters.platform}
        label="Platform"
        onChange={(e) => setFilters((p) => ({ ...p, platform: e.target.value }))}
      >
        <MenuItem value="">All</MenuItem>
        {platformOptions.map((plat) => (
          <MenuItem key={plat} value={plat}>{plat}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const rawChartData = data?.chart_data || [];
  
  // Transform chart data: one bar per model with year breakdown in tooltip
  const chartData = rawChartData.map(item => {
    const { model, ...years } = item;
    const total = Object.values(years).reduce((sum: number, val) => {
      const numVal = typeof val === 'number' ? val : Number(val || 0);
      return sum + numVal;
    }, 0);
    return {
      model: String(model),
      total,
      yearBreakdown: years
    };
  }).sort((a, b) => b.total - a.total); // Sort by total count descending

  return (
    <Box>
      <FilterBar filters={filters} setFilters={setFilters} onSearch={fetchData} loading={loading} onReset={handleReset} extraFilters={extraFilters} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <SummaryCard title="Total Vehicles" value={summary.total_vehicles} color="#2E77D0" />
          <SummaryCard title="Active" value={summary.active_vehicles} color="#43A047" />
          <SummaryCard title="Deleted / Defleeted" value={summary.deleted_vehicles} color="#E85D75" />
          <SummaryCard title="Unique Models" value={summary.unique_models} color="#FB8C00" />
        </Box>
      )}

      {/* Chart: Vehicle Count by Model */}
      {chartData.length > 0 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Vehicle Count by Model
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Click on any bar to see year-by-year breakdown
            </Typography>
            <Box sx={{ width: '100%', height: 500, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="model" 
                    angle={-45} 
                    textAnchor="end" 
                    interval={0}
                    tick={{ fontSize: 11 }}
                    tickMargin={30}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<SimpleModelTooltip />} />
                  <Bar 
                    dataKey="total" 
                    onClick={(data) => setSelectedModel(data)}
                    cursor="pointer"
                  >
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`}
                        fill={selectedModel?.model === entry.model ? '#0d47a1' : '#1976d2'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Year Breakdown Info Box */}
          {selectedModel && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f7fa', border: '2px solid #1976d2' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedModel.model}
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    Total: {selectedModel.total.toLocaleString()} vehicles
                  </Typography>
                </Box>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => setSelectedModel(null)}
                >
                  Close
                </Button>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Breakdown by Year:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {Object.entries(selectedModel.yearBreakdown || {})
                  .sort(([a], [b]) => b.localeCompare(a))
                  .map(([year, count]) => {
                    const percentage = ((Number(count) / selectedModel.total) * 100).toFixed(1);
                    return (
                      <Chip
                        key={year}
                        label={`${year}: ${Number(count).toLocaleString()} (${percentage}%)`}
                        variant="outlined"
                        color="primary"
                        sx={{ fontWeight: 500 }}
                      />
                    );
                  })}
              </Box>
            </Paper>
          )}
        </>
      )}

      {/* Table */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        {data?.csv_content && (
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadCsv(data.csv_content, 'fleet_composition.csv')}>
            Export CSV
          </Button>
        )}
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 520px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {['Make', 'Model', 'Model Year', 'Active', 'Deleted', 'Total'].map((col) => (
                <TableCell key={col} sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.8rem' }}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : !data?.data?.length ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><Typography color="text.secondary">No data</Typography></TableCell></TableRow>
            ) : (
              data.data.map((row, i) => (
                <TableRow key={`${row.model}-${row.model_year}-${i}`} hover>
                  <TableCell>{row.make}</TableCell>
                  <TableCell>{row.model}</TableCell>
                  <TableCell>{row.model_year}</TableCell>
                  <TableCell>{row.active_count.toLocaleString()}</TableCell>
                  <TableCell>{row.deleted_count.toLocaleString()}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.total_count.toLocaleString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// =============================================================================
// Tab 2: Usage & Odometer
// =============================================================================

function UsageOdometerTab() {
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTER_STATE);
  const [vehicleStatus, setVehicleStatus] = useState('');
  const [enrollmentStatus, setEnrollmentStatus] = useState('');
  const [tcuType, setTcuType] = useState('');
  const [pkg, setPkg] = useState('');
  const [pkgStatus, setPkgStatus] = useState('');
  const fetched = useRef(false);

  const [fleetSummary, setFleetSummary] = useState<FleetAnalyticsSummary | null>(null);
  const [fleetSummaryLoading, setFleetSummaryLoading] = useState(false);
  const fleetSummaryFetched = useRef(false);

  useEffect(() => {
    if (fleetSummaryFetched.current) return;
    fleetSummaryFetched.current = true;
    setFleetSummaryLoading(true);
    fetch('/api/fleet-analytics/usage-summary')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load fleet summary');
        return res.json();
      })
      .then(setFleetSummary)
      .catch((e) => console.error('Fleet summary error:', e))
      .finally(() => setFleetSummaryLoading(false));
  }, []);

  const fetchData = useCallback(async (pg = page, rpp = rowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | boolean> = {
        ...buildFilterParams(filters),
        limit: rpp,
        offset: pg * rpp,
      };
      if (vehicleStatus) params.status = vehicleStatus;
      if (enrollmentStatus) params.enrollment_status = enrollmentStatus;
      if (tcuType) params.tcu_type = tcuType;
      if (pkg) params.package = pkg;
      if (pkgStatus) params.package_status = pkgStatus;
      const result = await apiFetch<UsageResponse>('usage', params);
      setData(result);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage, vehicleStatus, enrollmentStatus, tcuType, pkg, pkgStatus]);

  useEffect(() => {
    if (!fetched.current) { fetched.current = true; fetchData(0, rowsPerPage); }
  }, [fetchData, rowsPerPage]);

  const handleSearch = () => { setPage(0); fetchData(0, rowsPerPage); };
  
  const handleReset = () => {
    setFilters(INITIAL_FILTER_STATE);
    setVehicleStatus('');
    setEnrollmentStatus('');
    setTcuType('');
    setPkg('');
    setPkgStatus('');
    setPage(0);
    setLoading(true);
    setError(null);
    const params: Record<string, string | number | boolean> = {
      ...buildFilterParams(INITIAL_FILTER_STATE),
      limit: rowsPerPage,
      offset: 0,
    };
    apiFetch<UsageResponse>('usage', params)
      .then(setData)
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const formatNum = (v: number | null | undefined) =>
    v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 1 }) : 'N/A';

  const s = data?.summary;
  const platformOptions = data?.by_platform ? Object.keys(data.by_platform).sort() : [];
  const extraFilters = (
    <>
      <FormControl size="small" sx={{ width: 160 }}>
        <InputLabel>Platform</InputLabel>
        <Select
          value={filters.platform}
          label="Platform"
          onChange={(e) => setFilters((p) => ({ ...p, platform: e.target.value }))}
        >
          <MenuItem value="">All</MenuItem>
          {platformOptions.map((plat) => (
            <MenuItem key={plat} value={plat}>{plat}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ width: 180 }}>
        <InputLabel>Vehicle Status</InputLabel>
        <Select value={vehicleStatus} label="Vehicle Status" onChange={(e) => setVehicleStatus(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {s?.by_vehicle_status && Object.keys(s.by_vehicle_status).sort().map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ width: 200 }}>
        <InputLabel>Enrollment</InputLabel>
        <Select value={enrollmentStatus} label="Enrollment" onChange={(e) => setEnrollmentStatus(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {s?.by_enrollment && Object.keys(s.by_enrollment).sort().map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Connection</InputLabel>
        <Select value={tcuType} label="Connection" onChange={(e) => setTcuType(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {s?.by_connection && Object.keys(s.by_connection).sort().map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ width: 200 }}>
        <InputLabel>Package</InputLabel>
        <Select value={pkg} label="Package" onChange={(e) => setPkg(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {s?.by_package && Object.keys(s.by_package).sort().map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Pkg Status</InputLabel>
        <Select value={pkgStatus} label="Pkg Status" onChange={(e) => setPkgStatus(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {s?.by_package_status && Object.keys(s.by_package_status).sort().map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );

  return (
    <Box>
      <FilterBar filters={filters} setFilters={setFilters} onSearch={handleSearch} loading={loading} onReset={handleReset} extraFilters={extraFilters} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Fleet-wide Summary Cards (from Neon batch data) */}
      {fleetSummaryLoading && !fleetSummary && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {fleetSummary?.summary && (() => {
        const fs = fleetSummary.summary;
        return (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <SummaryCard
                title="Avg Monthly Miles"
                value={formatNum(fs.avg_monthly_miles)}
                color="#2E77D0"
                subtitle={`Based on ${fs.vehicles_with_miles.toLocaleString()} of ${fs.total_vehicles.toLocaleString()} vehicles (fleet-wide)`}
              />
              <SummaryCard
                title="Avg Days Driven / Month"
                value={formatNum(fs.avg_days_driven)}
                color="#43A047"
                subtitle={`Based on ${fs.vehicles_with_days.toLocaleString()} of ${fs.total_vehicles.toLocaleString()} vehicles (fleet-wide)`}
              />
              <SummaryCard
                title="Avg Defleeting Odometer"
                value={formatNum(fs.avg_defleeting_odometer)}
                color="#E85D75"
                subtitle={`Based on ${fs.vehicles_with_defleeting_odo.toLocaleString()} of ${fs.total_deleted.toLocaleString()} defleeted vehicles (fleet-wide)`}
              />
            </Box>
            {fleetSummary.last_computed_at && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Fleet-wide data last computed: {new Date(fleetSummary.last_computed_at).toLocaleString()}
              </Typography>
            )}
          </>
        );
      })()}

      {/* Metrics by Model Table (from Neon batch data) */}
      {fleetSummary?.metrics_by_model && fleetSummary.metrics_by_model.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Metrics by Model (Fleet-wide)
          </Typography>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Model', 'Total', 'Not Deleted', 'Deleted', 'w/ Miles Data', 'Avg Miles/Mo', 'w/ Defleet Odo', 'Avg Defleet Odo'].map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {fleetSummary.metrics_by_model.map((row) => (
                  <TableRow key={row.model} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{row.model}</TableCell>
                    <TableCell>{row.total.toLocaleString()}</TableCell>
                    <TableCell>{row.active.toLocaleString()}</TableCell>
                    <TableCell>{row.deleted.toLocaleString()}</TableCell>
                    <TableCell>{row.with_miles_data.toLocaleString()}</TableCell>
                    <TableCell>{row.avg_miles != null ? row.avg_miles.toLocaleString(undefined, { maximumFractionDigits: 1 }) : 'N/A'}</TableCell>
                    <TableCell>{row.with_defleeting_odo.toLocaleString()}</TableCell>
                    <TableCell>{row.avg_defleeting_odo != null ? row.avg_defleeting_odo.toLocaleString(undefined, { maximumFractionDigits: 0 }) : 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Defleet Forecast (from Neon batch data — fleet-wide) */}
      {fleetSummary?.defleet_forecast && fleetSummary.defleet_forecast.coverage.total_active > 0 && (() => {
        const f = fleetSummary.defleet_forecast;
        const p = f.predictions;
        const c = f.coverage;
        return (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Defleet Forecast (Fleet-wide)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Based on {c.predictable.toLocaleString()} of {c.total_active.toLocaleString()} active vehicles
              {c.missing_odometer > 0 && ` \u2022 ${c.missing_odometer.toLocaleString()} missing odometer`}
              {c.missing_threshold > 0 && ` \u2022 ${c.missing_threshold.toLocaleString()} missing threshold`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <SummaryCard title="Past Threshold" value={p.past_threshold} color="#B71C1C" />
              <SummaryCard title="In 1 Month" value={p.month_1} color="#E85D75" />
              <SummaryCard title="In 2 Months" value={p.month_2} color="#FB8C00" />
              <SummaryCard title="In 3 Months" value={p.month_3} color="#2E77D0" />
            </Box>
            {f.by_model.length > 0 && (
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['Model', 'Defleet Threshold', 'Active', 'Predictable', 'Past', '1 Mo', '2 Mo', '3 Mo'].map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {f.by_model.map((row) => (
                      <TableRow key={row.model} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{row.model}</TableCell>
                        <TableCell>{row.defleet_threshold != null ? row.defleet_threshold.toLocaleString(undefined, { maximumFractionDigits: 0 }) : 'N/A'}</TableCell>
                        <TableCell>{row.active.toLocaleString()}</TableCell>
                        <TableCell>{row.predictable.toLocaleString()}</TableCell>
                        <TableCell sx={{ color: row.past_threshold > 0 ? '#B71C1C' : 'text.primary', fontWeight: row.past_threshold > 0 ? 600 : 400 }}>
                          {row.past_threshold.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ color: row.month_1 > 0 ? '#E85D75' : 'text.primary', fontWeight: row.month_1 > 0 ? 600 : 400 }}>
                          {row.month_1.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ color: row.month_2 > 0 ? '#FB8C00' : 'text.primary', fontWeight: row.month_2 > 0 ? 600 : 400 }}>
                          {row.month_2.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ color: row.month_3 > 0 ? '#2E77D0' : 'text.primary', fontWeight: row.month_3 > 0 ? 600 : 400 }}>
                          {row.month_3.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        );
      })()}

      {/* Table */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        {data?.csv_content && (
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadCsv(data.csv_content, 'usage_odometer.csv')}>
            Export CSV
          </Button>
        )}
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 560px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {['VIN', 'Make', 'Model', 'Year', 'Platform', 'Package', 'Pkg Status', 'Vehicle Status', 'Enrollment', 'Connection', 'Deleted', 'Last Odometer', 'Avg Miles/Mo', 'Avg Days/Mo'].map((col) => (
                <TableCell key={col} sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data ? (
              <TableRow><TableCell colSpan={14} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : !data?.data?.length ? (
              <TableRow><TableCell colSpan={14} align="center" sx={{ py: 6 }}><Typography color="text.secondary">No data</Typography></TableCell></TableRow>
            ) : (
              data.data.map((row, i) => (
                <TableRow key={row.vin + i} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.vin}</TableCell>
                  <TableCell>{row.make}</TableCell>
                  <TableCell>{row.model}</TableCell>
                  <TableCell>{row.model_year}</TableCell>
                  <TableCell>{row.platform || '-'}</TableCell>
                  <TableCell>{row.package}</TableCell>
                  <TableCell>{row.package_status}</TableCell>
                  <TableCell>{row.vehicle_status}</TableCell>
                  <TableCell>{row.enrollment_status}</TableCell>
                  <TableCell>{row.connection || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.is_deleted ? 'Yes' : 'No'}
                      size="small"
                      color={row.is_deleted ? 'error' : 'success'}
                      variant="outlined"
                      sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>{formatNum(row.last_odometer)}</TableCell>
                  <TableCell>{formatNum(row.avg_miles_per_month)}</TableCell>
                  <TableCell>{formatNum(row.avg_days_per_month)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data && (
        <TablePagination
          component="div"
          count={data.total_count}
          page={page}
          onPageChange={(_, p) => { setPage(p); fetchData(p, rowsPerPage); }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { const rpp = parseInt(e.target.value, 10); setRowsPerPage(rpp); setPage(0); fetchData(0, rpp); }}
          rowsPerPageOptions={[50, 100, 250, 500]}
        />
      )}
    </Box>
  );
}

// =============================================================================
// Tab 3: Fleet Movement
// =============================================================================

function FleetMovementTab() {
  const [data, setData] = useState<MovementResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTER_STATE);
  const fetched = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildFilterParams(filters);
      const result = await apiFetch<MovementResponse>('movement', params);
      setData(result);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!fetched.current) { fetched.current = true; fetchData(); }
  }, [fetchData]);

  const handleReset = () => {
    setFilters(INITIAL_FILTER_STATE);
    setLoading(true);
    setError(null);
    apiFetch<MovementResponse>('movement', buildFilterParams(INITIAL_FILTER_STATE))
      .then(setData)
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const summary = data?.summary;
  const models = data?.models || [];
  const byPlatform = data?.by_platform;
  const platformOptions = byPlatform ? Object.keys(byPlatform).sort() : [];
  const extraFilters = (
    <FormControl size="small" sx={{ width: 160 }}>
      <InputLabel>Platform</InputLabel>
      <Select
        value={filters.platform}
        label="Platform"
        onChange={(e) => setFilters((p) => ({ ...p, platform: e.target.value }))}
      >
        <MenuItem value="">All</MenuItem>
        {platformOptions.map((plat) => (
          <MenuItem key={plat} value={plat}>{plat}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <FilterBar filters={filters} setFilters={setFilters} onSearch={fetchData} loading={loading} onReset={handleReset} extraFilters={extraFilters} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <SummaryCard title="Total Added" value={summary.total_added} color="#43A047" />
          <SummaryCard title="Total Deleted" value={summary.total_deleted} color="#E85D75" />
          <SummaryCard
            title="Net Change"
            value={summary.net_change >= 0 ? `+${summary.net_change.toLocaleString()}` : summary.net_change.toLocaleString()}
            color={summary.net_change >= 0 ? '#43A047' : '#E85D75'}
          />
        </Box>
      )}

      {/* Chart: Monthly Additions vs Deletions */}
      {data?.chart_monthly && data.chart_monthly.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Monthly Additions vs Deletions
          </Typography>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.chart_monthly} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="added" fill="#43A047" name="Added" />
              <Bar dataKey="deleted" fill="#E85D75" name="Deleted" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Table */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        {data?.csv_content && (
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadCsv(data.csv_content, 'fleet_movement.csv')}>
            Export CSV
          </Button>
        )}
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 560px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {['Month', 'Model', 'Year', 'Added', 'Deleted', 'Net'].map((col) => (
                <TableCell key={col} sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.8rem' }}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : !data?.data?.length ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><Typography color="text.secondary">No data</Typography></TableCell></TableRow>
            ) : (
              data.data.map((row, i) => (
                <TableRow key={`${row.month}-${row.model}-${i}`} hover>
                  <TableCell>{row.month}</TableCell>
                  <TableCell>{row.model}</TableCell>
                  <TableCell>{row.model_year}</TableCell>
                  <TableCell sx={{ color: '#43A047', fontWeight: 600 }}>+{row.added.toLocaleString()}</TableCell>
                  <TableCell sx={{ color: '#E85D75', fontWeight: 600 }}>-{row.deleted.toLocaleString()}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: row.net >= 0 ? '#43A047' : '#E85D75' }}>
                    {row.net >= 0 ? '+' : ''}{row.net.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// =============================================================================
// Tab 4: Fleet Health
// =============================================================================

const INDICATOR_LABELS: Record<string, string> = {
  airbagWarning: 'Airbag Warning',
  engineWarning: 'Engine Warning',
  oilPressureWarning: 'Oil Pressure Warning',
  waterTemperatureWarning: 'Water Temperature Warning',
  powerLimitationAlert: 'Power Limitation Alert',
  batteryHighWarning: 'Battery High Warning',
  batteryLowWarning: 'Battery Low Warning',
  afuFailure: 'AFU Failure',
  parkWarning: 'Park Warning',
};

function FleetHealthTab() {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetched = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch<HealthResponse>('health', {});
      setData(result);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetched.current) { fetched.current = true; fetchData(); }
  }, [fetchData]);

  const fleet = data?.fleet_breakdown;
  const coverage = data?.health_coverage;
  const health = data?.health_summary;

  const indicatorRows = health
    ? Object.entries(health.indicator_counts)
        .sort(([, a], [, b]) => b - a)
    : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {fleet && (
        <>
          {/* Summary Cards — single row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <SummaryCard title="Total CCS2 Vehicles" value={fleet.total_ccs2} />
            <SummaryCard title="Active" value={fleet.non_defleeted} color="#43A047" />
            <SummaryCard title="Defleeted" value={fleet.defleeted} color="#E85D75" />
            <SummaryCard title="With Health Data" value={coverage?.with_health_data ?? 0} color="#2E77D0" />
            <SummaryCard title="Without Health Data" value={coverage?.without_health_data ?? 0} color="#546E7A" />
            <SummaryCard title="Current Alerts" value={health?.vehicles_with_alerts ?? 0} color={health?.vehicles_with_alerts ? '#E85D75' : '#43A047'} />
            <SummaryCard title="Alerts (30d)" value={data?.history_30d?.unique_vins_with_alerts ?? 0} color={data?.history_30d?.unique_vins_with_alerts ? '#FB8C00' : '#43A047'} />
          </Box>

          {/* Two-column layout: Current State | Last 30 Days */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            {/* Left: Current indicator breakdown + active alert VINs */}
            <Box sx={{ flex: '1 1 400px', minWidth: 0 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Current Malfunction Indicators
                </Typography>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.78rem' }}>Indicator</TableCell>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.78rem' }}>Vehicles</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {indicatorRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">No active alerts</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        indicatorRows.map(([key, count]) => (
                          <TableRow key={key} hover>
                            <TableCell>{INDICATOR_LABELS[key] || key}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: count > 0 ? '#E85D75' : 'text.primary' }}>
                              {count.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {data?.alert_vins && data.alert_vins.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                      VINs with Active Alerts
                    </Typography>
                    <TableContainer sx={{ maxHeight: 200 }}>
                      <Table size="small">
                        <TableBody>
                          {data.alert_vins.map((vin, i) => (
                            <TableRow key={vin} hover>
                              <TableCell sx={{ fontSize: '0.75rem', py: 0.5, color: 'text.secondary' }}>{i + 1}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', py: 0.5 }}>{vin}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Paper>
            </Box>

            {/* Right: 30-day indicator breakdown */}
            {data?.history_30d && Object.keys(data.history_30d.indicator_counts).length > 0 && (
              <Box sx={{ flex: '1 1 400px', minWidth: 0 }}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Last 30 Days — Alerts by Indicator
                  </Typography>
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.78rem' }}>Indicator</TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.78rem' }}>Unique Vehicles</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(data.history_30d.indicator_counts)
                          .sort(([, a], [, b]) => b - a)
                          .map(([key, count]) => (
                            <TableRow key={key} hover>
                              <TableCell>{INDICATOR_LABELS[key] || key}</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#E85D75' }}>
                                {count.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            )}
          </Box>

          {/* Full-width: 30-day Alert Details table */}
          {data?.history_30d && data.history_30d.alert_details.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Health Alert Details — Last 30 Days
              </Typography>
              <TableContainer sx={{ maxHeight: 450 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['VIN', 'Indicator', 'First Seen', 'Last Seen', 'Events'].map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.history_30d.alert_details.map((row, i) => (
                      <TableRow key={`${row.vin}-${row.indicator}-${i}`} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.vin}</TableCell>
                        <TableCell>{INDICATOR_LABELS[row.indicator] || row.indicator}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                          {new Date(row.first_seen).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                          {new Date(row.last_seen).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.event_count.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export default function FleetIntelligencePage() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsightsIcon fontSize="large" />
          Fleet Intelligence
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Hertz fleet insights — composition, usage, movement trends, and health
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Fleet Composition" />
        <Tab label="Usage & Odometer" />
        <Tab label="Fleet Movement" />
        <Tab label="Fleet Health" />
      </Tabs>

      {/* Tab Content */}
      <Box>
        {tab === 0 && <FleetCompositionTab />}
        {tab === 1 && <UsageOdometerTab />}
        {tab === 2 && <FleetMovementTab />}
        {tab === 3 && <FleetHealthTab />}
      </Box>
    </Box>
  );
}
